import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { HomeStatus } from '@toro/common/shared/enum/home-status.enum';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { StorageService } from '@toro/common/shared/services/storage.service';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';

import {
  canonicalSituation,
  HOME_SITUATION_TRACKER,
  parseStoredSituation,
  resolveSituationLabel,
  resolveSituationMovement,
} from './home-situation-tracker.model';

/** Contexto opcional de uma amostra de situação da home. */
export interface SituationTrackContext {
  /** Identidade do usuário (ex.: CBLC): isola o estado entre contas na mesma aba. */
  userKey?: string;
  productAreaOrigin?: string;
  trigger?: string;
}

interface SituationChange extends SituationTrackContext {
  situation: HomeStatus;
}

/**
 * Dispara `Element Loaded` quando a situação da home muda entre uma visita e
 * outra (anterior, nova e avanço/retrocesso). Debounce evita o "pisca" da
 * corrida de perfis (SKY-1765); dedup evita repetir em remontagem; só emite
 * quando há situação anterior. A última situação fica em sessionStorage por
 * usuário (sobrevive a F5 e à volta de outra área na mesma aba, sem cruzar
 * dados entre contas diferentes).
 */
@Injectable({ providedIn: 'root' })
export class HomeSituationTrackerService implements OnDestroy {
  private readonly situations$ = new Subject<SituationChange>();
  private readonly subscription: Subscription;
  private lastSituation: HomeStatus | null = null;
  private currentUserKey: string | null = null;

  constructor(private store: Store<AppState>) {
    this.subscription = this.situations$
      .pipe(
        debounceTime(HOME_SITUATION_TRACKER.debounceMs),
        distinctUntilChanged(
          (a, b) =>
            this.normalizeUserKey(a.userKey) === this.normalizeUserKey(b.userKey) &&
            canonicalSituation(a.situation) === canonicalSituation(b.situation)
        )
      )
      .subscribe((change) => this.evaluate(change));
  }

  /**
   * Registra a situação atual da home. Pode ser chamado muitas vezes por
   * carregamento: o tracker cuida de debounce, deduplicação e de só emitir
   * quando há mudança real.
   */
  public track(
    situation: HomeStatus,
    context: SituationTrackContext = {}
  ): void {
    if (situation === null || situation === undefined) {
      return;
    }
    this.situations$.next({ situation, ...context });
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private evaluate(change: SituationChange): void {
    const userKey = this.normalizeUserKey(change.userKey);
    this.resetOnUserChange(userKey);

    const current = canonicalSituation(change.situation);
    const previous = this.lastSituation ?? this.readStoredSituation(userKey);

    this.lastSituation = current;
    this.persistSituation(userKey, current);

    if (previous === null || previous === current) {
      return;
    }
    this.emit(previous, current, change.productAreaOrigin, change.trigger);
  }

  /** Zera o estado em memória quando o usuário muda na mesma aba/sessão. */
  private resetOnUserChange(userKey: string | null): void {
    if (userKey !== this.currentUserKey) {
      this.currentUserKey = userKey;
      this.lastSituation = null;
    }
  }

  private normalizeUserKey(userKey?: string): string | null {
    return userKey ? userKey : null;
  }

  private storageKeyFor(userKey: string | null): string {
    return userKey
      ? `${HOME_SITUATION_TRACKER.storageKey}.${userKey}`
      : HOME_SITUATION_TRACKER.storageKey;
  }

  private readStoredSituation(userKey: string | null): HomeStatus | null {
    try {
      const stored = parseStoredSituation(
        StorageService.getSessionItem(this.storageKeyFor(userKey))
      );
      return stored === null ? null : canonicalSituation(stored);
    } catch {
      return null;
    }
  }

  private persistSituation(userKey: string | null, situation: HomeStatus): void {
    try {
      StorageService.setSessionItem(
        this.storageKeyFor(userKey),
        String(situation)
      );
    } catch {
      // sessionStorage indisponível: segue apenas com o estado em memória.
    }
  }

  private emit(
    previous: HomeStatus,
    next: HomeStatus,
    productAreaOrigin?: string,
    trigger?: string
  ): void {
    const events = {
      'Product Area Origin':
        productAreaOrigin || HOME_SITUATION_TRACKER.productAreaOriginFallback,
      'Time Responsável': HOME_SITUATION_TRACKER.timeResponsavel,
      Element: HOME_SITUATION_TRACKER.element,
      'Situação Anterior': resolveSituationLabel(previous),
      'Situação Nova': resolveSituationLabel(next),
      Gatilho: trigger || HOME_SITUATION_TRACKER.defaultTrigger,
      'Specific Properties': {
        movimento: resolveSituationMovement(previous, next),
      },
    };

    this.store.dispatch({
      type: TrackingActions.trackEvents,
      payload: { type: ActionEventsEnum.ELEMENT_LOADED, events },
    });
  }
}
